import { FC, useEffect, useRef } from 'react';
import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { BooleanConversation, Conversation } from 'src/services/useChat';
import ReactMarkdown from 'react-markdown';
import { LoadingText } from './LoadingText';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import CodeBlock from 'src/components/CodeBlock';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { updateCopiedBooleanQuery } from 'src/features/chatNow';
import { useAlert } from 'src/hooks/useAlert';

interface ChatListProps {
  conversation: (Conversation | BooleanConversation)[];
  sendingLoading?: boolean;
}

const ChatList: FC<ChatListProps> = ({
  conversation,
  sendingLoading = false
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { handleClickAlert } = useAlert();
  const { chatMode } = useAppSelector((state) => state.chatNow);

  const { position } = useAppSelector(
    (state) => state.storeAIAssistantPosition
  );

  const messageEndRef = useRef(null);

  const scrollToBottom = () => {
    if (messageEndRef.current) {
      messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCopyAIResponse = (message: string) => {
    navigator.clipboard.writeText(message);
    handleClickAlert({
      message: 'Text has Copied',
      severity: 'success',
      horizontal: 'center',
      vertical: 'top'
    });
  };

  const getResponseText = (
    message: Conversation | BooleanConversation
  ): string => {
    return 'answer' in message ? message.answer : message.response;
  };

  const handleCopyQuery = (message: BooleanConversation) => {
    dispatch(
      updateCopiedBooleanQuery({
        phrasesList: message.include,
        excludePhrases: message.exclude
      })
    );
    handleClickAlert({
      message: 'Boolean query copied successfully',
      severity: 'success',
      horizontal: 'center',
      vertical: 'top'
    });
  };

  const transformIncludeBooleanQueryToString = (arr: string[][]): string => {
    return arr
      .map((innerArr) => {
        if (innerArr.length > 1) {
          return `(${innerArr.map((str) => `"${str}"`).join(' AND ')})`;
        } else {
          return `"${innerArr[0]}"`;
        }
      })
      .join(' OR ');
  };

  const renderQuery = (message: Conversation | BooleanConversation) => {
    if ('include' in message && 'exclude' in message) {
      let query = '';
      query += transformIncludeBooleanQueryToString(message.include);
      if (message.exclude.length > 0) {
        query += ' NOT ';
        query += message.exclude.map((item) => `"${item}"`).join(' OR ');
      }
      return (
        <Stack gap={0.5} alignItems="flex-start">
          <CodeBlock code={query} />
          <IconButton onClick={() => handleCopyQuery(message)}>
            <Stack alignItems="center" spacing={1} direction="row">
              <ContentCopyIcon sx={{ fontSize: '16px', color: '#4B4B4B' }} />
              <Typography fontSize="12px">Copy</Typography>
            </Stack>
          </IconButton>
        </Stack>
      );
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [sendingLoading, conversation]);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth:
          position === 'full' ? '50%' : position === 'side' ? '350px' : '400px',
        height: 'calc(100vh - 200px)',
        overflowY: 'auto',
        pt: 2,
        px: 1,
        '&::-webkit-scrollbar': {
          width: '8px'
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1'
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px'
        }
      }}
    >
      {conversation.map((message, index) => (
        <Stack key={message.id} gap={3} mb={3}>
          {/* User Message */}
          <Stack direction="row" gap={1}>
            <Box>
              <AccountCircleIcon
                fontSize="medium"
                sx={{
                  color: theme.palette.primary.main,
                  border: '1px solid #BFBFBF',
                  borderRadius: '50%',
                  padding: '3px'
                }}
              />
            </Box>
            <Stack gap={0.5}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                You
              </Typography>
              <Typography variant="subtitle2">{message.question}</Typography>
            </Stack>
          </Stack>
          {/* AI Response */}
          <Stack direction="row" gap={1}>
            <Box>
              <AutoFixHighIcon
                fontSize="medium"
                sx={{
                  color: theme.palette.primary.main,
                  border: '1px solid #BFBFBF',
                  borderRadius: '50%',
                  padding: '3px'
                }}
              />
            </Box>
            <Stack gap={0.5}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Assistant
              </Typography>
              {sendingLoading && index === conversation.length - 1 ? (
                <LoadingText />
              ) : (
                <Stack gap={0.5} alignItems="flex-start">
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#2d2d2d'
                    }}
                  >
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => (
                          <Typography variant="subtitle2" gutterBottom>
                            {children}
                          </Typography>
                        )
                      }}
                    >
                      {getResponseText(message)}
                    </ReactMarkdown>
                  </Typography>
                  {chatMode !== 'booleanQuery' && (
                    <IconButton
                      onClick={() =>
                        handleCopyAIResponse(getResponseText(message))
                      }
                    >
                      <Stack alignItems="center" spacing={1} direction="row">
                        <ContentCopyIcon
                          sx={{ fontSize: '16px', color: '#4B4B4B' }}
                        />
                        <Typography fontSize="12px">Copy</Typography>
                      </Stack>
                    </IconButton>
                  )}
                </Stack>
              )}
              {chatMode === 'booleanQuery' ? renderQuery(message) : null}
            </Stack>
          </Stack>
        </Stack>
      ))}
      <Stack ref={messageEndRef}></Stack>
    </Box>
  );
};

export default ChatList;
